import * as THREE from 'three';
import { loadGlb } from '../assets/loadGlb';
import { placeInFront, SNAP_PLACE_DIST } from './Snap';

const LEANTO_GLB = '/models/bld/bld_leanto.glb';

/** Envelope: 2.0 wide × 1.6 tall × 1.4 deep. */
export const LEAN_TO_SIZE = { x: 2.0, y: 1.6, z: 1.4 } as const;
export const LEAN_TO_COST = { wood: 6, fiber: 4 } as const;
export const LEAN_TO_PLACE_DIST = SNAP_PLACE_DIST;

export type LeanToPose = { x: number; y: number; z: number; yaw: number };

const BARK = 0x5a3a22;
const THATCH = 0x8a7040;
const LASH = 0x6a5a38;
const GHOST = 0x7a9a58;

export function placeLeanToInFront(
  player: { x: number; y: number; z: number },
  yaw: number,
): LeanToPose {
  return placeInFront(player, yaw);
}

export function applyLeanToPose(obj: THREE.Object3D, pose: LeanToPose): void {
  obj.position.set(pose.x, pose.y, pose.z);
  obj.rotation.y = pose.yaw;
}

/** Thatch / bark card kit. Not a grey cube. */
export function createLeanTo(ghost = false): THREE.Group {
  const root = new THREE.Group();
  root.name = ghost ? 'CC-LEANTO-GHOST' : 'CC-LEANTO';
  root.userData.id = root.name;
  root.userData.size = { ...LEAN_TO_SIZE };

  const bark = makeMat(BARK, 0.94, ghost);
  const thatch = makeMat(THATCH, 0.97, ghost, 0.9);
  const lash = makeMat(LASH, 0.88, ghost);

  // Rear posts (taller) and front posts — bark sticks.
  pole(root, bark, -0.86, 0.78, -0.58, 1.56, 0.05);
  pole(root, bark, 0.86, 0.78, -0.58, 1.56, 0.05);
  pole(root, bark, -0.82, 0.42, 0.58, 0.84, 0.045);
  pole(root, bark, 0.82, 0.42, 0.58, 0.84, 0.045);

  // Ridge + eave poles.
  pole(root, bark, 0, 1.52, -0.58, 0.06, 0.04, 1.78, Math.PI / 2);
  pole(root, bark, 0, 0.82, 0.58, 0.05, 0.035, 1.7, Math.PI / 2);
  pole(root, lash, -0.86, 1.12, 0, 0.04, 0.03, 1.28, 0, 0.55);
  pole(root, lash, 0.86, 1.12, 0, 0.04, 0.03, 1.28, 0, 0.55);

  // Single-slope thatch cards.
  const slope = Math.atan2(1.52 - 0.82, 1.16);
  for (const [x, y, z, w, h] of [
    [-0.48, 1.22, -0.08, 0.92, 1.22],
    [0.48, 1.22, -0.08, 0.92, 1.22],
    [0.0, 1.28, -0.14, 0.7, 1.08],
    [-0.22, 1.08, 0.12, 0.86, 0.95],
    [0.28, 1.06, 0.16, 0.8, 0.9],
  ] as const) {
    card(root, thatch, w, h, x, y, z, -slope, 0, 0);
  }

  // Back wall bark cards.
  card(root, bark, 0.95, 1.15, -0.46, 0.72, -0.68, 0, 0, 0);
  card(root, bark, 0.95, 1.15, 0.46, 0.72, -0.68, 0, 0, 0);
  card(root, bark, 0.7, 0.55, 0, 0.38, -0.66, 0, 0, 0);

  // Side windbreak + floor bark slats.
  card(root, bark, 1.15, 0.7, -0.98, 0.5, 0, 0, Math.PI / 2, 0);
  card(root, thatch, 1.7, 1.15, 0, 0.03, 0, -Math.PI / 2, 0, 0);

  if (!ghost) {
    void dressLeanTo(root);
  }
  return root;
}

async function dressLeanTo(root: THREE.Group): Promise<void> {
  try {
    const hero = await loadGlb(LEANTO_GLB);
    hero.name = 'CC-LEANTO-HERO';
    hero.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
    for (const child of root.children) {
      child.visible = false;
    }
    root.add(hero);
  } catch {
    /* keep primitive lean-to */
  }
}

function makeMat(color: number, roughness: number, ghost: boolean, metalness = 0.03): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: ghost ? GHOST : color,
    roughness,
    metalness,
    transparent: ghost,
    opacity: ghost ? 0.34 : 1,
    depthWrite: !ghost,
    side: THREE.DoubleSide,
  });
}

function pole(
  parent: THREE.Object3D,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  height: number,
  radius: number,
  length = height,
  rotZ = 0,
  rotX = 0,
): void {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius * 1.15, length, 5), mat);
  mesh.position.set(x, y, z);
  mesh.rotation.z = rotZ;
  mesh.rotation.x = rotX;
  parent.add(mesh);
}

function card(
  parent: THREE.Object3D,
  mat: THREE.Material,
  w: number,
  h: number,
  x: number,
  y: number,
  z: number,
  rotX: number,
  rotY: number,
  rotZ: number,
): void {
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
  mesh.position.set(x, y, z);
  mesh.rotation.set(rotX, rotY, rotZ);
  parent.add(mesh);
}
